// @ts-nocheck
import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import Iconify from '../iconify';
import { InactiveUser } from './types';

interface InactiveUsersTableProps {
  title: string;
  subheader?: string;
  users: InactiveUser[];
}

export default function InactiveUsersTable({ title, subheader, users }: InactiveUsersTableProps) {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownload = () => {
    const headers = ['#', 'User', 'Status', 'Last Login', 'Queries'];
    const rows = users.map((user, i) => [
      i + 1,
      user.username,
      user.status === 'never_logged_in' ? 'Never Logged In' : 'Logged In - No Queries',
      user.lastLoginDate || 'N/A',
      user.totalQueries,
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inactive_users.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const neverLoggedIn = users.filter((u) => u.status === 'never_logged_in').length;
  const loggedInNoQueries = users.filter((u) => u.status === 'logged_in_no_queries').length;

  const paginatedUsers = users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Card>
      <CardHeader
        title={title}
        subheader={
          subheader ||
          `${users.length} inactive users — ${neverLoggedIn} never logged in, ${loggedInNoQueries} logged in but no queries`
        }
        action={
          <Tooltip title="Download CSV">
            <IconButton onClick={handleDownload}>
              <Iconify icon="mdi:download" />
            </IconButton>
          </Tooltip>
        }
      />
      <TableContainer sx={{ px: 2, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 500 }}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Last Login</TableCell>
              <TableCell align="right">Queries</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user, index) => {
              const globalIndex = page * rowsPerPage + index;
              const isNeverLoggedIn = user.status === 'never_logged_in';
              return (
                <TableRow key={user.username}>
                  <TableCell>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: theme.palette.grey[300],
                        color: theme.palette.text.secondary,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {globalIndex + 1}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">{user.username}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={isNeverLoggedIn ? 'Never Logged In' : 'No Queries'}
                      size="small"
                      sx={{
                        bgcolor: isNeverLoggedIn
                          ? alpha(theme.palette.error.main, 0.12)
                          : alpha(theme.palette.warning.main, 0.12),
                        color: isNeverLoggedIn ? theme.palette.error.main : theme.palette.warning.dark,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {user.lastLoginDate ? new Date(user.lastLoginDate).toLocaleDateString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{user.totalQueries}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={users.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Card>
  );
}
